import type { AutoHook, EditingScript, CaptionStyleConfig } from "@/remotion/schemas"
import { STYLE_PRESETS } from "@/lib/style-presets"

export type FontFamily =
  | 'Inter'
  | 'Arial'
  | 'Helvetica'
  | 'Georgia'
  | 'Times New Roman'
  | 'Courier New'
  | 'Montserrat'
  | 'Komika Axis'
  | 'Poppins'
  | 'Roboto'
  | 'Bebas Neue'
  | 'Oswald'
  | 'Spline Sans'
  | 'Bernina Sans'
export type FontWeight = 'normal' | 'bold' | '100' | '400' | '500' | '600' | '700' | '900'
export type FontStyle = 'normal' | 'italic'
export type TextAlign = 'left' | 'center' | 'right'
export type Position = 'top' | 'middle' | 'bottom' | 'auto' | 'custom'
export type TextTransform = 'none' | 'uppercase' | 'lowercase' | 'capitalize'
export type AnimationType = 'none' | 'fade' | 'slide' | 'pop' | 'karaoke' | 'karaoke-bg' | 'deep-diver' | 'scale'

export interface CaptionStyle {
  wordsPerCaption: number
  color: string
  backgroundColor: string
  fontSize: number
  fontFamily: string
  fontWeight: string
  fontStyle?: FontStyle
  textAlign: TextAlign
  strokeWidth: number
  strokeColor: string
  lineHeight: number
  position: Position
  positionOffset: number
  textTransform: TextTransform
  animation: AnimationType
  activeWordEnabled: boolean
  activeWordColor: string
  activeWordBackgroundColor: string
  activeWordStrokeWidth: number
  activeWordStrokeColor: string
  shadowEnabled?: boolean
  shadowColor?: string
  shadowBlur?: number
  shadowOffsetX?: number
  shadowOffsetY?: number
  keywordHighlightEnabled?: boolean
  keywordColor?: string
  aiEmojiEnabled?: boolean
  positionX?: number
  positionY?: number
  maxLines?: number | null
  maskProfanity?: boolean
}

export interface Word {
  word: string
  time_stamp: number      // End time in seconds
  start_time?: number     // Start time in seconds (for pause detection)
  start_position: number
  end_position: number
}

export interface Caption {
  id: string
  text: string
  startTime: number
  endTime: number
  words?: Word[]
}

export interface PublishRecord {
  id: string
  platform: string
  account_id: string
  account_name: string
  video_url: string
  video_id: string
  title: string
  description: string
  privacy: string
  published_at: string
  status: string
}

export interface ClipDetail {
  id: string
  video_id: string
  title: string
  clip_index: number
  start_time: number
  end_time: number
  duration: number
  virality_score: number
  virality_reason: string
  transcript_segment: string
  words: Word[]
  captions: Caption[]
  caption_style: CaptionStyle
  status: string
  preview_url?: string
  rendered_url?: string
  render_progress?: number
  render_error?: string
  // Publishing history
  publish_history?: PublishRecord[]
  // Opus-style track architecture
  auto_hook?: AutoHook | null
  editing_script?: EditingScript | null
  // Custom thumbnail
  thumbnail_url?: string | null
}

/**
 * Default caption style optimized for 9:16 vertical video
 *
 * Font sizing for vertical (1080px width) scaled to ~400px preview:
 * - scale ≈ 0.37, so base fontSize needs to be larger
 * - fontSize 50: renders at ~18px in preview, ~50px in export
 * - fontSize 60: renders at ~22px in preview, ~60px in export
 * - fontSize 70: renders at ~26px in preview, ~70px in export
 *
 * Position offset: % from edge
 * - 10-12%: Standard (leaves room for emoji overlays)
 * - 15-18%: More breathing room
 */
export const DEFAULT_CAPTION_STYLE: CaptionStyle = {
  wordsPerCaption: 5,
  color: '#FFFFFF',
  backgroundColor: 'transparent',
  fontSize: 54,  // Actual render size in 1920px-tall video (preview scales down)
  fontFamily: 'Montserrat',
  fontWeight: '900',
  fontStyle: 'normal',
  textAlign: 'center',
  strokeWidth: 7,
  strokeColor: '#000000',
  lineHeight: 1.15,
  position: 'bottom',
  positionOffset: 10,
  textTransform: 'uppercase',
  animation: 'karaoke',
  activeWordEnabled: true,
  activeWordColor: '#04F827',
  activeWordBackgroundColor: '',
  activeWordStrokeWidth: 6,
  activeWordStrokeColor: '#000000',
  shadowEnabled: true,
  shadowColor: '#000000',
  shadowBlur: 3,
  shadowOffsetX: 2,
  shadowOffsetY: 2,
}

export const FONT_OPTIONS: FontFamily[] = [
  'Inter',
  'Arial',
  'Helvetica',
  'Georgia',
  'Times New Roman',
  'Courier New',
  'Montserrat',
  'Komika Axis',
  'Poppins',
  'Roboto',
  'Bebas Neue',
  'Oswald',
  'Spline Sans',
]

export const POSITION_OPTIONS: { value: Position; label: string }[] = [
  { value: 'top', label: 'Top' },
  { value: 'middle', label: 'Middle' },
  { value: 'bottom', label: 'Bottom' },
]

export const COLOR_PRESETS = [
  '#FFFFFF',
  '#FF0000',
  '#00FF00',
  '#0000FF',
  '#FFFF00',
  '#FF00FF',
  '#00FFFF',
  '#000000',
  '#888888',
  '#FF8800',
]

const configToCaptionStyle = (config: Partial<CaptionStyleConfig>): CaptionStyle => {
  const font = config.font ?? {}
  const colors = config.colors ?? {}
  const stroke = config.stroke ?? {}
  const shadow = config.shadow ?? {}
  const backdrop = config.backdrop ?? {}

  return {
    wordsPerCaption: config.wordsPerCaption ?? DEFAULT_CAPTION_STYLE.wordsPerCaption,
    color: colors.default ?? DEFAULT_CAPTION_STYLE.color,
    backgroundColor: backdrop.enabled ? (backdrop.color ?? "rgba(0,0,0,0.55)") : "transparent",
    fontSize: font.size ?? DEFAULT_CAPTION_STYLE.fontSize,
    fontFamily: font.family ?? DEFAULT_CAPTION_STYLE.fontFamily,
    fontWeight: font.weight ?? DEFAULT_CAPTION_STYLE.fontWeight,
    fontStyle: (font.style as FontStyle | undefined) ?? DEFAULT_CAPTION_STYLE.fontStyle,
    textAlign: "center",
    strokeWidth: stroke.enabled ? (stroke.width ?? 0) : 0,
    strokeColor: stroke.color ?? DEFAULT_CAPTION_STYLE.strokeColor,
    lineHeight: config.lineHeight ?? DEFAULT_CAPTION_STYLE.lineHeight,
    position: (config.position as Position | undefined) ?? DEFAULT_CAPTION_STYLE.position,
    positionOffset: config.positionOffset ?? DEFAULT_CAPTION_STYLE.positionOffset,
    textTransform: (config.textTransform as TextTransform | undefined) ?? DEFAULT_CAPTION_STYLE.textTransform,
    animation: (config.animation as AnimationType | undefined) ?? DEFAULT_CAPTION_STYLE.animation,
    activeWordEnabled: config.activeWordEnabled ?? DEFAULT_CAPTION_STYLE.activeWordEnabled,
    activeWordColor: colors.primary ?? DEFAULT_CAPTION_STYLE.activeWordColor,
    activeWordBackgroundColor:
      config.animation === "karaoke-bg"
        ? (colors.secondary ?? "#FFFD03")
        : "",
    activeWordStrokeWidth: stroke.enabled ? Math.max(0, Math.min((stroke.width ?? 0) - 1, 8)) : 0,
    activeWordStrokeColor: stroke.color ?? DEFAULT_CAPTION_STYLE.activeWordStrokeColor,
    shadowEnabled: shadow.enabled ?? DEFAULT_CAPTION_STYLE.shadowEnabled,
    shadowColor: shadow.color ?? DEFAULT_CAPTION_STYLE.shadowColor,
    shadowBlur: shadow.blur ?? DEFAULT_CAPTION_STYLE.shadowBlur,
    shadowOffsetX: shadow.x ?? DEFAULT_CAPTION_STYLE.shadowOffsetX,
    shadowOffsetY: shadow.y ?? DEFAULT_CAPTION_STYLE.shadowOffsetY,
  }
}

export const CAPTION_TEMPLATES: { name: string; style: CaptionStyle }[] = STYLE_PRESETS.map((preset) => ({
  name: preset.name,
  style: configToCaptionStyle(preset.captionStyle),
}))
